import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn } from '../common/utils';
import { createTimestampTransformer } from '../common/sql';
import {
  DBTaxonomyQuestionCountSchema,
  DBTopicAliasSchema,
  DBTopicMigrationSchema,
  DBTopicMigrationTargetSchema,
  DBTopicQuestionSummarySchema,
  DBTopicRelatedSpecificSchema,
  DBTopicSchema,
  DBTopicSubjectSchema,
  DBTopicSubjectSyllabusSchema,
  TaxonomyQuestionCountSchema,
  TopicAliasSchema,
  TopicMigrationSchema,
  TopicMigrationTargetSchema,
  TopicQuestionSummarySchema,
  TopicRelatedSpecificSchema,
  TopicSchema,
  TopicSubjectSchema,
  TopicSubjectSyllabusSchema,
} from './schemas';

export const TopicCodecConfig: TransformerConfig<typeof TopicSchema, typeof DBTopicSchema> = {
  name: 'Topic',
  clientSchema: TopicSchema,
  dbSchema: DBTopicSchema,
  transformers: [
    createTimestampTransformer<typeof TopicSchema, typeof DBTopicSchema>('createdAt'),
    createTimestampTransformer<typeof TopicSchema, typeof DBTopicSchema>('updatedAt', {
      nullable: true,
    }),
  ],
};

export const TopicAliasCodecConfig: TransformerConfig<
  typeof TopicAliasSchema,
  typeof DBTopicAliasSchema
> = {
  name: 'TopicAlias',
  clientSchema: TopicAliasSchema,
  dbSchema: DBTopicAliasSchema,
  transformers: [
    createTimestampTransformer<typeof TopicAliasSchema, typeof DBTopicAliasSchema>('createdAt'),
  ],
};

export const TopicSubjectCodecConfig: TransformerConfig<
  typeof TopicSubjectSchema,
  typeof DBTopicSubjectSchema
> = {
  name: 'TopicSubject',
  clientSchema: TopicSubjectSchema,
  dbSchema: DBTopicSubjectSchema,
  transformers: [
    createTimestampTransformer<typeof TopicSubjectSchema, typeof DBTopicSubjectSchema>('createdAt'),
    createTimestampTransformer<typeof TopicSubjectSchema, typeof DBTopicSubjectSchema>(
      'updatedAt',
      {
        nullable: true,
      }
    ),
  ],
};

export const TopicSubjectSyllabusCodecConfig: TransformerConfig<
  typeof TopicSubjectSyllabusSchema,
  typeof DBTopicSubjectSyllabusSchema
> = {
  name: 'TopicSubjectSyllabus',
  clientSchema: TopicSubjectSyllabusSchema,
  dbSchema: DBTopicSubjectSyllabusSchema,
  transformers: [
    createTimestampTransformer<
      typeof TopicSubjectSyllabusSchema,
      typeof DBTopicSubjectSyllabusSchema
    >('createdAt'),
  ],
};

export const TopicRelatedSpecificCodecConfig: TransformerConfig<
  typeof TopicRelatedSpecificSchema,
  typeof DBTopicRelatedSpecificSchema
> = {
  name: 'TopicRelatedSpecific',
  clientSchema: TopicRelatedSpecificSchema,
  dbSchema: DBTopicRelatedSpecificSchema,
  transformers: [
    createTimestampTransformer<
      typeof TopicRelatedSpecificSchema,
      typeof DBTopicRelatedSpecificSchema
    >('createdAt'),
  ],
};

export const TopicMigrationCodecConfig: TransformerConfig<
  typeof TopicMigrationSchema,
  typeof DBTopicMigrationSchema
> = {
  name: 'TopicMigration',
  clientSchema: TopicMigrationSchema,
  dbSchema: DBTopicMigrationSchema,
  transformers: [
    createTimestampTransformer<typeof TopicMigrationSchema, typeof DBTopicMigrationSchema>(
      'createdAt'
    ),
  ],
};

export const TopicMigrationTargetCodecConfig: TransformerConfig<
  typeof TopicMigrationTargetSchema,
  typeof DBTopicMigrationTargetSchema
> = {
  name: 'TopicMigrationTarget',
  clientSchema: TopicMigrationTargetSchema,
  dbSchema: DBTopicMigrationTargetSchema,
  transformers: [
    createTimestampTransformer<
      typeof TopicMigrationTargetSchema,
      typeof DBTopicMigrationTargetSchema
    >('createdAt'),
  ],
};

export const TopicQuestionSummaryCodecConfig: TransformerConfig<
  typeof TopicQuestionSummarySchema,
  typeof DBTopicQuestionSummarySchema
> = {
  name: 'TopicQuestionSummary',
  clientSchema: TopicQuestionSummarySchema,
  dbSchema: DBTopicQuestionSummarySchema,
  transformers: [],
};

export const TaxonomyQuestionCountCodecConfig: TransformerConfig<
  typeof TaxonomyQuestionCountSchema,
  typeof DBTaxonomyQuestionCountSchema
> = {
  name: 'TaxonomyQuestionCount',
  clientSchema: TaxonomyQuestionCountSchema,
  dbSchema: DBTaxonomyQuestionCountSchema,
  transformers: [],
};

export const encodeTopicToDb = createEncodeToDbFn(TopicCodecConfig);
export const decodeTopicFromDb = createDecodeFromDbFn(TopicCodecConfig);
export const encodeTopicAliasToDb = createEncodeToDbFn(TopicAliasCodecConfig);
export const decodeTopicAliasFromDb = createDecodeFromDbFn(TopicAliasCodecConfig);
export const encodeTopicSubjectToDb = createEncodeToDbFn(TopicSubjectCodecConfig);
export const decodeTopicSubjectFromDb = createDecodeFromDbFn(TopicSubjectCodecConfig);
export const encodeTopicSubjectSyllabusToDb = createEncodeToDbFn(TopicSubjectSyllabusCodecConfig);
export const decodeTopicSubjectSyllabusFromDb = createDecodeFromDbFn(
  TopicSubjectSyllabusCodecConfig
);
export const encodeTopicRelatedSpecificToDb = createEncodeToDbFn(TopicRelatedSpecificCodecConfig);
export const decodeTopicRelatedSpecificFromDb = createDecodeFromDbFn(
  TopicRelatedSpecificCodecConfig
);
export const encodeTopicMigrationToDb = createEncodeToDbFn(TopicMigrationCodecConfig);
export const decodeTopicMigrationFromDb = createDecodeFromDbFn(TopicMigrationCodecConfig);
export const encodeTopicMigrationTargetToDb = createEncodeToDbFn(TopicMigrationTargetCodecConfig);
export const decodeTopicMigrationTargetFromDb = createDecodeFromDbFn(
  TopicMigrationTargetCodecConfig
);
export const decodeTopicQuestionSummaryFromDb = createDecodeFromDbFn(
  TopicQuestionSummaryCodecConfig
);
export const decodeTaxonomyQuestionCountFromDb = createDecodeFromDbFn(
  TaxonomyQuestionCountCodecConfig
);
